import type { CallInfo } from '@compass/client/types/call';
import { GenericTable } from '@components/GenericTable';
import { Check, Phone, Play } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { AudioPlayerDialog } from '@/components/AudioPlayerDialog';
import { FormattedDateTime } from '@/components/FormattedDateTime';
import { Breadcrumb, BreadcrumbPortal } from '@/components/Header';
import { Button } from '@/components/ui/button';
import { SimpleTooltip } from '@/components/ui/tooltip';
import { useActiveCall } from '@/hooks/useActiveCall';
import { useMyCallHistory } from '@/hooks/useMyCallHistory';
import type { TableConfig } from '@/types/table';
import { isCaseLinked } from '@/utils/calls';
import { formatCallDuration } from '@/utils/date';

export function CallsContainer() {
  const navigate = useNavigate();

  const { calls, isLoading } = useMyCallHistory(true);

  // Fetch the active (in-progress) call
  const { activeCall } = useActiveCall(true);

  // Merge active call as the first row, deduplicating from history
  const tableData = useMemo(() => {
    if (!activeCall) return calls;
    const filtered = calls.filter(c => c.id !== activeCall.id);
    return [activeCall, ...filtered];
  }, [activeCall, calls]);

  // Audio dialog state
  const [audioDialogCall, setAudioDialogCall] = useState<CallInfo | null>(null);

  // Build table config for CallInfo data
  const tableConfig = useMemo((): TableConfig<CallInfo> => {
    return {
      columns: [
        {
          key: 'reporterPhone',
          header: 'Reporter Phone',
          accessor: item => {
            const phone = item.input_data?.reporter_phone;
            const isLive = !!activeCall && item.id === activeCall.id;
            return (
              <span className="flex items-center gap-2">
                {phone ? (
                  <span className="whitespace-nowrap font-mono text-xs text-foreground">
                    {phone}
                  </span>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
                {isLive && (
                  <span className="inline-flex items-center gap-1.5 text-xs font-medium text-red-500">
                    <span className="relative flex h-2 w-2">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" />
                      <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
                    </span>
                    Live
                  </span>
                )}
              </span>
            );
          },
        },
        {
          key: 'contactId',
          header: 'Contact ID',
          accessor: item => {
            const contactId = item.input_data?.contact_id;
            return contactId ? (
              <span className="whitespace-nowrap">{contactId}</span>
            ) : (
              <span className="text-muted-foreground">—</span>
            );
          },
        },
        {
          key: 'patientName',
          header: 'Name',
          accessor: item => {
            const name = item.extracted_data?.input?.data?.patient_full_name;
            return name ? (
              <span className="whitespace-nowrap">{name}</span>
            ) : (
              <span className="text-muted-foreground">—</span>
            );
          },
        },
        {
          key: 'patientDob',
          header: 'DOB',
          accessor: item => {
            const dob = item.extracted_data?.input?.data?.patient_dob;
            return dob ? (
              <span className="whitespace-nowrap">{dob}</span>
            ) : (
              <span className="text-muted-foreground">—</span>
            );
          },
        },
        {
          key: 'startTime',
          header: 'Start Time',
          accessor: item => {
            const time = item.started_at || item.input_data?.call_start_time || item.created_at;
            return <FormattedDateTime value={time} className="whitespace-nowrap text-foreground" />;
          },
        },
        {
          key: 'duration',
          header: 'Duration',
          accessor: item => {
            const duration = formatCallDuration(
              item.input_data?.call_start_time,
              item.input_data?.call_end_time,
            );
            return <span>{duration}</span>;
          },
        },
        {
          key: 'actions',
          header: '',
          accessor: item => {
            const hasCaseRecord = !!item.caserecord_id;
            const hasAssets = !!item.assets?.length;
            const isLinked = isCaseLinked(item);

            return (
              <div className="flex items-center gap-2">
                <SimpleTooltip content={!hasAssets ? 'Recording in Progress' : ''} side="top">
                  <Button
                    variant="outline"
                    size="xs"
                    className={`gap-1 ${!hasAssets ? 'cursor-not-allowed opacity-40' : ''}`}
                    disabled={!hasAssets}
                    onClick={e => {
                      e.stopPropagation();
                      e.preventDefault();
                      setAudioDialogCall(item);
                    }}
                  >
                    <Play className="h-3 w-3" />
                  </Button>
                </SimpleTooltip>
                {isLinked ? (
                  <Button variant="outline" size="xs" className="gap-1" disabled>
                    <Check className="h-3 w-3" />
                    Case Created
                  </Button>
                ) : (
                  <Button
                    variant="default"
                    size="xs"
                    className="gap-1"
                    disabled={!hasCaseRecord}
                    onClick={e => {
                      e.stopPropagation();
                      e.preventDefault();
                      if (item.caserecord_id) {
                        navigate(`../create-case?caseRecordId=${item.caserecord_id}`);
                      }
                    }}
                  >
                    Create Case
                  </Button>
                )}
              </div>
            );
          },
        },
      ],
      getRowKey: item => item.id || '',
      emptyState: {
        icon: <Phone className="h-10 w-10 text-muted-foreground" />,
        title: 'No calls found',
        description: 'There are no recent calls to display.',
      },
    };
  }, [navigate, activeCall]);

  return (
    <>
      <BreadcrumbPortal>
        <Breadcrumb>
          <Breadcrumb.Item>My Calls</Breadcrumb.Item>
        </Breadcrumb>
      </BreadcrumbPortal>

      <div className="flex flex-col h-full">
        <div className="flex-1 px-6 overflow-hidden">
          <div className="flex flex-col h-full min-h-0">
            <GenericTable
              config={tableConfig}
              data={tableData}
              loading={isLoading}
              currentPage={0}
              hasNextPage={false}
              onPageChange={() => {}}
              selectionMode={false}
              hidePagination
            />
          </div>
        </div>
      </div>

      <AudioPlayerDialog
        open={!!audioDialogCall}
        onOpenChange={open => {
          if (!open) setAudioDialogCall(null);
        }}
        call={audioDialogCall}
      />
    </>
  );
}
